import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useAccountContext } from "@providers/AccountProvider";
import { tourRegistry } from "@/tours/registry";
import { useTourStates, useSetTourState } from "@/tours/use-tour-persistence";
import { readTourStates, writeTourState } from "@/tours/tour-persistence";
import { getUserTrack } from "@/tours/tour-triggers";
import type { TourConfig, TourId, PersistedTourState, TourStates, TourTrack } from "@/tours/types";
import { trackPLGEvent } from "@common/lib/plgTracking";

type TourBlocker = () => string | null;

type TourContextValue = {
    startTour: (tourId: TourId) => boolean;
    stopTour: () => void;
    activeTour: TourConfig | null;
    stepIndex: number;
    run: boolean;
    setStepIndex: (n: number) => void;
    pauseTour: () => void;
    resumeTour: () => void;
    getTourState: (tourId: TourId) => PersistedTourState | undefined;
    tourStatesLoaded: boolean;
    helpMenuTours: TourConfig[];
    markCompleted: (tourId: TourId, stepCount: number) => void;
    markDismissed: (tourId: TourId, stepIndex: number) => void;
    registerTourBlocker: (id: string, check: TourBlocker) => void;
    unregisterTourBlocker: (id: string) => void;
    /** Advance to the next step via TourEngine's advanceToStep (handles groups, navigation, waitForTarget). */
    advanceToNextStep: () => void;
    /** Called by TourEngine to register its advanceToStep function. */
    registerAdvanceStep: (fn: (() => void) | null) => void;
};

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
    const session = useSession();
    const userId = session?.user?.id;
    const { accountData } = useAccountContext();

    const queryClient = useQueryClient();
    const { data: tourStates, isFetched: tourStatesLoaded } = useTourStates(userId);
    const setTourStateMutation = useSetTourState(userId);
    const mutateTourStateRef = useRef(setTourStateMutation.mutate);
    mutateTourStateRef.current = setTourStateMutation.mutate;

    const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
    const [stepIndex, setStepIndex] = useState(0);
    const [run, setRun] = useState(false);
    const tourBlockersRef = useRef<Map<string, TourBlocker>>(new Map());

    const activeTour = activeTourId ? (tourRegistry[activeTourId] ?? null) : null;

    const track: TourTrack | null = useMemo(() => getUserTrack(accountData), [accountData]);

    const helpMenuTours = useMemo(() => {
        if (!track) return [];
        return (Object.values(tourRegistry).filter(Boolean) as TourConfig[]).filter(
            (tour) => tour.showInHelpMenu && (!tour.track || tour.track === track),
        );
    }, [track]);

    const getTourState = useCallback((tourId: TourId) => tourStates?.[tourId], [tourStates]);

    const markCompleted = useCallback(
        (tourId: TourId, stepCount: number) => {
            if (!userId) {
                console.warn(`[Tours] Cannot persist completed state for "${tourId}" — no userId`);
                return;
            }
            if (tourId === "welcome" || tourId === "quick-start") {
                trackPLGEvent("welcome_tour_completed", { skipped: false, tourId });
            }
            mutateTourStateRef.current({
                tourId,
                state: {
                    status: "completed",
                    lastStepIndex: stepCount - 1,
                    completedAt: new Date().toISOString(),
                    dismissedAt: null,
                },
            });
        },
        [userId],
    );

    const markDismissed = useCallback(
        (tourId: TourId, currentStepIndex: number) => {
            if (!userId) {
                console.warn(`[Tours] Cannot persist dismissed state for "${tourId}" — no userId`);
                return;
            }
            // Check freshest cache to prevent a race: if markCompleted and markDismissed fire in quick
            // succession (e.g., user finishes tour and Joyride fires SKIPPED after FINISHED), the
            // dismissed callback's stale closure could overwrite the completed state.
            const cached = queryClient.getQueryData<TourStates>(["tour-state", userId]);
            if (cached?.[tourId]?.status === "completed") return;

            if (tourId === "welcome" || tourId === "quick-start") {
                trackPLGEvent("welcome_tour_completed", { skipped: true, tourId });
            }
            mutateTourStateRef.current({
                tourId,
                state: {
                    status: "dismissed",
                    lastStepIndex: currentStepIndex,
                    completedAt: null,
                    dismissedAt: new Date().toISOString(),
                },
            });
        },
        [queryClient, userId],
    );

    const registerTourBlocker = useCallback((id: string, check: TourBlocker) => {
        tourBlockersRef.current.set(id, check);
    }, []);

    const unregisterTourBlocker = useCallback((id: string) => {
        tourBlockersRef.current.delete(id);
    }, []);

    // Ref bridge: TourEngine registers its advanceToStep here so content components can advance.
    const advanceStepFnRef = useRef<(() => void) | null>(null);
    const registerAdvanceStep = useCallback((fn: (() => void) | null) => {
        advanceStepFnRef.current = fn;
    }, []);
    const advanceToNextStep = useCallback(() => {
        advanceStepFnRef.current?.();
    }, []);

    const startTour = useCallback(
        (tourId: TourId): boolean => {
            if (activeTourId) {
                console.warn(`[Tours] Cannot start "${tourId}" — tour "${activeTourId}" is already active`);
                return false;
            }

            // Check all registered blockers — fail-closed: a broken blocker blocks the tour
            const blockers = Array.from(tourBlockersRef.current.entries());
            for (let i = 0; i < blockers.length; i++) {
                const [blockerId, check] = blockers[i];
                let message: string | null = null;
                try {
                    message = check();
                } catch (e) {
                    console.error(`[Tours] Blocker "${blockerId}" threw during check:`, e);
                    Sentry.captureException(e, { extra: { blockerId, tourId } });
                    toast.warn("Unable to start tour right now. Please try again.", { autoClose: 4000 });
                    return false;
                }
                if (message) {
                    console.warn(`[Tours] Cannot start "${tourId}" — blocked by "${blockerId}": ${message}`);
                    toast.info(message, { autoClose: 4000 });
                    return false;
                }
            }

            const config = tourRegistry[tourId];
            if (!config) {
                console.warn(`[Tours] Tour "${tourId}" not found in registry`);
                Sentry.captureMessage(`Tour "${tourId}" not found in registry`, {
                    level: "error",
                    extra: { tourId },
                });
                return false;
            }

            setActiveTourId(tourId);
            setStepIndex(0);
            setRun(true);
            return true;
        },
        [activeTourId],
    );

    const stopTour = useCallback(() => {
        setActiveTourId(null);
        setStepIndex(0);
        setRun(false);
    }, []);

    const pauseTour = useCallback(() => setRun(false), []);
    const resumeTour = useCallback(() => setRun(true), []);

    // Mark the active tour dismissed on page refresh/close, recording the step the user stopped at.
    // Uses synchronous localStorage write (not the async mutation) because the browser
    // cancels async operations during beforeunload.
    // stepIndex is read from a ref to avoid re-registering the listener on every step change,
    // which would create brief gaps where no handler is registered.
    const stepIndexRef = useRef(stepIndex);
    stepIndexRef.current = stepIndex;
    useEffect(() => {
        if (!activeTourId || !userId) return;
        const handleBeforeUnload = () => {
            try {
                // Don't overwrite a completed status — there's a brief window between markCompleted
                // writing to localStorage and React re-rendering to remove this listener.
                const existing = readTourStates(userId);
                if (existing[activeTourId as keyof typeof existing]?.status === "completed") return;

                writeTourState(userId, activeTourId, {
                    status: "dismissed",
                    lastStepIndex: stepIndexRef.current,
                    completedAt: null,
                    dismissedAt: new Date().toISOString(),
                });
            } catch (e) {
                // Best-effort — localStorage may be full or restricted.
                // Note: Sentry.captureException would not reliably send during beforeunload
                // (browser tears down the page before the HTTP request completes), so we only log.
                console.warn("[Tours] Failed to persist tour state on beforeunload:", e);
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [activeTourId, userId]);

    const value = useMemo<TourContextValue>(
        () => ({
            startTour,
            stopTour,
            activeTour,
            stepIndex,
            run,
            setStepIndex,
            pauseTour,
            resumeTour,
            getTourState,
            tourStatesLoaded,
            helpMenuTours,
            markCompleted,
            markDismissed,
            registerTourBlocker,
            unregisterTourBlocker,
            advanceToNextStep,
            registerAdvanceStep,
        }),
        [
            startTour,
            stopTour,
            activeTour,
            stepIndex,
            run,
            pauseTour,
            resumeTour,
            getTourState,
            tourStatesLoaded,
            helpMenuTours,
            markCompleted,
            markDismissed,
            registerTourBlocker,
            unregisterTourBlocker,
            advanceToNextStep,
            registerAdvanceStep,
        ],
    );

    return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/** Warn + report when a tour method is called outside the provider tree. */
function noopWarn(method: string, extra?: Record<string, unknown>): void {
    console.warn(`[Tours] ${method}() called outside TourProvider tree — no tour context available`);
    Sentry.captureMessage(`Tour ${method} called outside TourProvider tree`, {
        level: "warning",
        extra,
    });
}

const NOOP_TOUR_CONTEXT: TourContextValue = {
    startTour: (tourId: TourId) => {
        noopWarn("startTour", { tourId });
        return false;
    },
    stopTour: () => noopWarn("stopTour"),
    activeTour: null,
    stepIndex: 0,
    run: false,
    setStepIndex: () => noopWarn("setStepIndex"),
    pauseTour: () => noopWarn("pauseTour"),
    resumeTour: () => noopWarn("resumeTour"),
    getTourState: () => {
        noopWarn("getTourState");
        return undefined;
    },
    tourStatesLoaded: false,
    helpMenuTours: [],
    markCompleted: (tourId) => noopWarn("markCompleted", { tourId }),
    markDismissed: (tourId) => noopWarn("markDismissed", { tourId }),
    registerTourBlocker: (id) => noopWarn("registerTourBlocker", { blockerId: id }),
    unregisterTourBlocker: (id) => noopWarn("unregisterTourBlocker", { blockerId: id }),
    advanceToNextStep: () => noopWarn("advanceToNextStep"),
    registerAdvanceStep: () => noopWarn("registerAdvanceStep"),
};

let hasWarnedMissingProvider = false;
export function useTour() {
    const ctx = useContext(TourContext);
    // All app content renders inside TourProvider (PermissionWrapper.tsx), so this fallback should be unreachable
    if (!ctx && !hasWarnedMissingProvider) {
        hasWarnedMissingProvider = true;
        console.error("[Tours] useTour() called outside TourProvider tree — tours will not function.");
        Sentry.captureMessage("useTour called outside TourProvider tree", { level: "error" });
    }
    return ctx ?? NOOP_TOUR_CONTEXT;
}
